import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Brain, Coins, ArrowRight } from '@phosphor-icons/react';
import api from '../lib/api';
import DailyLessonModal from './DailyLessonModal';
import DailyQuizModal from './DailyQuizModal';

const DailyLearningCard = () => {
  const [data, setData] = useState({ lesson: null, viewed_today: false });
  const [quizData, setQuizData] = useState({ quiz: null, completed_today: false });
  const [showLesson, setShowLesson] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  const refreshAll = async () => {
    try {
      const [l, q] = await Promise.all([
        api.get('/learning/daily-lesson'),
        api.get('/learning/daily-quiz'),
      ]);
      setData(l.data);
      setQuizData(q.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  if (!data.lesson) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
        data-testid="daily-learning-card"
      >
        {/* Daily Lesson Card */}
        <button
          onClick={() => setShowLesson(true)}
          className="group glass-card p-5 text-left hover:scale-[1.01] transition-transform border border-yellow-500/20 hover:border-yellow-500/40 relative overflow-hidden"
          data-testid="open-daily-lesson"
        >
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-yellow-500/10 blur-2xl" />
          <div className="flex items-start gap-3 relative">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
              <BookOpen size={22} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-400">📚 Today's Lesson</p>
                {!data.viewed_today && (
                  <span className="px-1.5 py-0.5 rounded-full bg-yellow-500 text-black text-[10px] font-bold">NEW</span>
                )}
              </div>
              <h3 className="text-white font-bold text-base truncate">{data.lesson.title}</h3>
              <p className="text-zinc-400 text-xs mt-0.5 line-clamp-1">— {data.lesson.author}</p>
            </div>
            <ArrowRight size={20} className="text-zinc-500 group-hover:text-gold group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
          </div>
        </button>

        {/* Daily Quiz Card */}
        <button
          onClick={() => setShowQuiz(true)}
          className="group glass-card p-5 text-left hover:scale-[1.01] transition-transform border border-purple-500/20 hover:border-purple-500/40 relative overflow-hidden"
          data-testid="open-daily-quiz"
        >
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-purple-500/10 blur-2xl" />
          <div className="flex items-start gap-3 relative">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center flex-shrink-0">
              <Brain size={22} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs font-bold uppercase tracking-wider text-purple-400">🧠 Daily Quiz</p>
                {quizData.quiz?.difficulty && (
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                      quizData.quiz.difficulty === 'easy'   ? 'bg-emerald-500/20 text-emerald-300' :
                      quizData.quiz.difficulty === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                                                              'bg-red-500/20 text-red-300'
                    }`}
                  >
                    {quizData.quiz.difficulty}
                  </span>
                )}
                {!quizData.completed_today && (
                  <span className="px-1.5 py-0.5 rounded-full bg-purple-500 text-white text-[10px] font-bold">+$50</span>
                )}
                {quizData.completed_today && (
                  <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">DONE</span>
                )}
              </div>
              <h3 className="text-white font-bold text-base truncate">{quizData.quiz?.theme || 'Loading...'}</h3>
              <p className="text-zinc-400 text-xs mt-0.5 flex items-center gap-1">
                <Coins size={12} weight="fill" className="text-gold" />
                Perfect score earns +$50
              </p>
            </div>
            <ArrowRight size={20} className="text-zinc-500 group-hover:text-gold group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
          </div>
        </button>
      </motion.div>

      {showLesson && (
        <DailyLessonModal
          lesson={data.lesson}
          onClose={() => { setShowLesson(false); refreshAll(); }}
        />
      )}
      {showQuiz && quizData.quiz && (
        <DailyQuizModal
          quiz={quizData.quiz}
          alreadyDone={quizData.completed_today}
          onClose={() => { setShowQuiz(false); refreshAll(); }}
        />
      )}
    </>
  );
};

export default DailyLearningCard;
